import { useState, useCallback, useRef } from 'react';
import type { FileUpload } from '../types';
import { fileProcessor } from '../services/fileProcessor';
import { Upload, X, FileText, Image, File, AlertCircle, CheckCircle } from 'lucide-react';

interface FileUploaderProps {
  onFilesProcessed: (files: FileUpload[]) => void;
  maxFiles?: number;
  acceptedTypes?: string;
  maxFileSize?: number; // in bytes
  className?: string;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  onFilesProcessed,
  maxFiles = 5,
  maxFileSize = 10 * 1024 * 1024,
  className = '',
}) => {
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize) {
      return `File ${file.name} is too large. Max size is ${(maxFileSize / 1024 / 1024).toFixed(0)}MB.`;
    }
    if (!fileProcessor.isSupportedFile(file)) {
      return `File ${file.name} has an unsupported format.`;
    }
    return null;
  };

  const processFiles = useCallback(
    async (fileList: FileList) => {
      const newFiles = Array.from(fileList).slice(0, maxFiles - files.length);
      const validationErrors: string[] = [];
      const processedFiles: FileUpload[] = [];

      for (const file of newFiles) {
        const error = validateFile(file);
        if (error) {
          validationErrors.push(error);
          continue;
        }

        setProcessing(true);
        try {
          const processed = await fileProcessor.processFile(file);
          processedFiles.push(processed);
        } catch (err) {
          validationErrors.push(`Failed to process ${file.name}: ${(err as Error).message}`);
        } finally {
          setProcessing(false);
        }
      }

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
      }

      const updatedFiles = [...files, ...processedFiles];
      setFiles(updatedFiles);
      onFilesProcessed(updatedFiles);
    },
    [files, maxFiles, onFilesProcessed]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setErrors([]);
      processFiles(e.dataTransfer.files);
    },
    [processFiles]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        setErrors([]);
        processFiles(e.target.files);
      }
    },
    [processFiles]
  );

  const removeFile = (id: string) => {
    const updated = files.filter((f) => f.id !== id);
    setFiles(updated);
    onFilesProcessed(updated);
  };

  const getFileIcon = (type: string) => {
    if (type === 'pdf') return <FileText className="w-5 h-5 text-red-500" />;
    if (type === 'image') return <Image className="w-5 h-5 text-blue-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  return (
    <div className={className}>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-2xl p-8 md:p-12 text-center cursor-pointer transition-all duration-200 ${
          isDragging
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleInputChange}
          className="hidden"
          accept=".pdf,.html,.htm,.txt,.md,.png,.jpg,.jpeg,.webp,.gif"
        />
        <div className="space-y-3">
          <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <Upload className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {processing ? 'Processing files...' : 'Drag and drop files here'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              or click to browse (PDF, HTML, Images, Text)
            </p>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Max {maxFiles} files, {(maxFileSize / 1024 / 1024).toFixed(0)}MB each
          </p>
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="mt-4 space-y-2">
          {errors.map((error, index) => (
            <div
              key={index}
              className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2.5 rounded-lg"
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
              <button
                onClick={() => setErrors((prev) => prev.filter((_, i) => i !== index))}
                className="ml-auto p-0.5 hover:bg-red-100 dark:hover:bg-red-900/40 rounded"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              {getFileIcon(file.type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {file.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {file.processed ? (
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      Processed
                    </span>
                  ) : (
                    'Processing pending...'
                  )}
                </p>
              </div>
              {file.processed && file.content && (
                <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                  {(file.content.length / 1024).toFixed(1)}KB
                </span>
              )}
              <button
                onClick={() => removeFile(file.id)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUploader;
